<template>
  <v-card flat>
    <v-list-item>
      <v-list-item-content two-line class="pt-0">
        <v-list-item-title :class="['headline', dividerClass + '--text']">
          {{ title }}</v-list-item-title
        >
        <v-list-item-subtitle>{{
          $t('personneMorale.detail.title.sub')
        }}</v-list-item-subtitle>
      </v-list-item-content>

      <v-list-item-action v-if="!control.noPrint">
        <printButton />
      </v-list-item-action>
    </v-list-item>

    <v-form v-model="valid" lazy-validation ref="form">
      <v-card-text class="pt-0">
        <v-row dense>
          <v-col cols="12" md="6" sm="12">
            <v-text-field
              dense
              filled
              v-model="form.nomUsuel"
              :rules="[obligatoireRule(), serveurRule('nomUsuel')]"
              :label="$t('personneMorale.detail.form.nomUsuel')"
              :readonly="control.readonly"
              hide-details="auto"
              :background-color="analyseDiff.color['nomUsuel']"
              :hint="analyseDiff.hint['nomUsuel']"
            />
          </v-col>
          <v-col cols="12" md="6" sm="12">
            <v-text-field
              dense
              filled
              v-model="form.raisonSociale"
              :label="$t('personneMorale.detail.form.raisonSociale')"
              :readonly="control.readonly"
              hide-details="auto"
              :background-color="analyseDiff.color['raisonSociale']"
              :hint="analyseDiff.hint['raisonSociale']"
              :rules="[obligatoireRule(), serveurRule('raisonSociale')]"
            />
          </v-col>
        </v-row>
        <v-row dense>
          <v-col cols="12" md="2" v-if="form.id">
            <v-text-field
              dense
              filled
              v-model="form.id"
              :label="$t('personneMorale.detail.form.id')"
              readonly
              hide-details="auto"
            />
          </v-col>
          <v-col cols="12" md="5">
            <v-select
              return-object
              dense
              filled
              multiple
              chip
              v-model="form.contextes"
              :items="listeContextes"
              prepend-inner-icon="mdi-layers-triple"
              item-text="libelle"
              item-value="id"
              :label="$t('personneMorale.detail.form.contextes')"
              :background-color="analyseDiff.color['contextes']"
              :readonly="control.readonly"
            >
              <template #selection="{item}">
                <v-chip
                  small
                  :color="
                    analyseDiff.color[`contextes[${item.id}]`] || 'secondary'
                  "
                >
                  <v-avatar
                    left
                    v-if="analyseDiff.supprime[`contextes[${item.id}]`]"
                  >
                    <v-btn
                      icon
                      @click.stop.capture="
                        annulerSuppression(`contextes[${item.id}]`)
                      "
                    >
                      <v-icon color="white">undo</v-icon>
                    </v-btn>
                  </v-avatar>

                  {{ item.libelle }}</v-chip
                >
              </template>
            </v-select>
          </v-col>
          <v-col>
            <v-tooltip bottom :disabled="control.readonly">
              <template #activator="{ on }">
                <v-text-field
                  dense
                  filled
                  v-on="on"
                  v-model="form.sirenOuSiret"
                  :label="$t('personneMorale.detail.form.sirenOuSiret')"
                  :readonly="control.readonly"
                  hide-details="auto"
                  :background-color="analyseDiff.color['sirenOuSiret']"
                  :hint="analyseDiff.hint['sirenOuSiret']"
                  :rules="[obligatoireRule(), serveurRule('sirenOuSiret')]"
                />
              </template>
              <span>
                {{ $t('personneMorale.detail.form.sirenOuSiretTooltip') }}
              </span>
            </v-tooltip>
          </v-col>
        </v-row>
        <v-row dense>
          <v-col cols="12" md="4">
            <v-text-field
              dense
              filled
              v-model="form.mail"
              prepend-inner-icon="mdi-email"
              :label="$t('personneMorale.detail.form.mail')"
              :readonly="control.readonly"
              hide-details="auto"
              :background-color="analyseDiff.color['mail']"
              :hint="analyseDiff.hint['mail']"
              :rules="[serveurRule('mail')]"
            />
          </v-col>
          <v-col cols="12" md="3" sm="12">
            <vue-tel-input-vuetify
              enabledCountryCode="true"
              mode="international"
              :v-mask="getmask(form.fax)"
              :preferredCountries="['FR']"
              defaultCountry="FR"
              disabledFetchingCountry
              type="tel"
              dense
              filled
              v-model="form.fax"
              :label="$t('personneMorale.detail.form.fax')"
              :readonly="control.readonly"
              hide-details="auto"
              :disabled="control.readonly"
              :background-color="analyseDiff.color['fax']"
              :hint="analyseDiff.hint['fax']"
              :rules="[serveurRule('fax')]"
            >
              <template v-slot:append>
                <v-icon>
                  mdi-fax
                </v-icon>
              </template>
            </vue-tel-input-vuetify>
          </v-col>
          <v-col cols="12" md="5" sm="12">
            <v-autocomplete
              return-object
              dense
              filled
              :clearable="!control.readonly"
              v-model="form.type"
              :items="listeTypesPM"
              item-text="libelle"
              item-value="id"
              :label="$t('personneMorale.detail.form.type')"
              :backgroundColor="analyseDiff.color['type']"
              :hint="analyseDiff.hint['type']"
              :readonly="control.readonly"
              hide-details="auto"
            />
          </v-col>
        </v-row>
        <v-row dense>
          <v-col cols="12" md="6" sm="12">
            <v-card>
              <v-card-title>
                <v-icon>mdi-bank</v-icon>
                {{
                  $tc(
                    'personneMorale.detail.form.maisonsMeres.title',
                    form.maisonsMeres ? form.maisonsMeres.length : 0,
                  )
                }}
                <v-spacer></v-spacer>
                <v-btn
                  v-if="!control.readonly"
                  text
                  small
                  outlined
                  color="secondary"
                  @click="selectMaisonMere"
                  >{{ $t('personneMorale.detail.form.maisonsMeres.add') }}
                </v-btn>
                <dialogRecherchePersonneMorale
                  ref="dialogRechercheMaisonMere"
                  :subtitle="
                    $t(
                      'personneMorale.detail.form.maisonsMeres.dialog.description',
                    )
                  "
                  @input="addMaisonMere"
                />
              </v-card-title>
              <v-card-text>
                <v-list dense>
                  <template v-for="(maisonMere, index) in form.maisonsMeres">
                    <v-list-item
                      :key="`maisonMere${maisonMere.id}`"
                      v-show="index < 3 ? true : showAllMaisonsMeres"
                    >
                      <v-list-item-content>
                        <v-list-item-title>
                          <a @click="detailFiliale(maisonMere.id)">
                            {{ maisonMere.nomUsuel }}
                          </a>
                        </v-list-item-title>
                      </v-list-item-content>

                      <v-list-item-action
                        v-if="
                          analyseDiff.supprime[
                            `maisonsMeres[${maisonMere.id}]`
                          ] && !control.readonly
                        "
                      >
                        <v-btn
                          icon
                          :color="colorMaisonMere(maisonMere.id)"
                          @click.stop="
                            annulerSuppression(`maisonsMeres[${maisonMere.id}]`)
                          "
                        >
                          <v-icon>undo</v-icon>
                        </v-btn>
                      </v-list-item-action>

                      <v-list-item-action v-else-if="!control.readonly">
                        <v-btn
                          small
                          icon
                          @click.stop="removeMaisonMere(maisonMere, index)"
                        >
                          <v-icon>mdi-bank-remove</v-icon></v-btn
                        >
                      </v-list-item-action>
                    </v-list-item>
                    <v-divider
                      v-if="
                        maisonMere &&
                          ((showAllMaisonsMeres &&
                            index < form.maisonsMeres.length - 1) ||
                            index <
                              Math.min(
                                form.maisonsMeres.length,
                                nbFilialesVisibles,
                              ) -
                                1)
                      "
                      :key="`${maisonMere.id}`"
                      :class="dividerClass"
                    />
                  </template>
                </v-list>
                <v-btn
                  icon
                  v-if="
                    form.maisonsMeres &&
                      form.maisonsMeres.length > nbFilialesVisibles
                  "
                  v-on:click="showAllMaisonsMeres = !showAllMaisonsMeres"
                >
                  <v-icon>mdi-dots-horizontal</v-icon>
                </v-btn>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" md="6" sm="12">
            <v-card>
              <v-card-title>
                <v-icon>mdi-domain</v-icon>
                {{
                  $tc(
                    'personneMorale.detail.form.filiales.title',
                    filiales ? filiales.length : 0,
                  )
                }}
              </v-card-title>
              <v-card-text>
                <v-list dense>
                  <template v-for="(filiale, index) in filiales">
                    <v-list-item
                      :key="`filiale${filiale.id}`"
                      v-show="index < 3 ? true : showAllFiliales"
                    >
                      <v-list-item-content>
                        <v-list-item-title>
                          <a @click="detailFiliale(filiale.id)">
                            {{ filiale.nomUsuel }}
                          </a>
                        </v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                    <v-divider
                      v-if="
                        filiale &&
                          ((showAllFiliales && index < filiales.length - 1) ||
                            index <
                              Math.min(filiales.length, nbFilialesVisibles) - 1)
                      "
                      :key="`${filiale.id}`"
                      :class="dividerClass"
                    />
                  </template>
                </v-list>
                <v-btn
                  icon
                  v-if="filiales && filiales.length > nbFilialesVisibles"
                  v-on:click="showAllFiliales = !showAllFiliales"
                >
                  <v-icon>mdi-dots-horizontal</v-icon>
                </v-btn>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
        <v-row dense v-if="filiales && filiales.length > 3">
          <v-col cols="12" md="12" sm="12"> </v-col>
        </v-row>
        <v-row dense>
          <v-col cols="12" md="6" sm="12">
            <v-checkbox
              v-model="form.domicilieeMDL"
              :label="$t('personneMorale.detail.form.domicilieeMDL')"
              :readonly="control.readonly"
              :background-color="analyseDiff.color['domicilieeMDL']"
              :hint="analyseDiff.hint['domicilieeMDL']"
            />
          </v-col>
          <v-col cols="12" md="6" sm="12">
            <v-checkbox
              v-model="form.organisationPro"
              :label="$t('personneMorale.detail.form.organisationPro')"
              :readonly="control.readonly"
              :background-color="analyseDiff.color['organisationPro']"
              :hint="analyseDiff.hint['organisationPro']"
            />
          </v-col>
        </v-row>
        <v-row dense>
          <v-col cols="12" md="12" sm="12">
            <v-select
              return-object
              dense
              filled
              multiple
              chip
              v-model="form.cotiseA"
              :items="listeCotiseA"
              item-text="libelle"
              item-value="id"
              :label="$t('personneMorale.detail.form.cotiseA')"
              :readonly="control.readonly"
            >
              <template #selection="{item}">
                <v-chip
                  small
                  :color="
                    analyseDiff.color[`cotiseA[${item.id}]`] || 'secondary'
                  "
                >
                  <v-avatar
                    left
                    v-if="analyseDiff.supprime[`cotiseA[${item.id}]`]"
                  >
                    <v-btn
                      icon
                      @click.stop.capture="
                        annulerSuppression(`cotiseA[${item.id}]`)
                      "
                    >
                      <v-icon color="white">undo</v-icon>
                    </v-btn>
                  </v-avatar>

                  {{ item.libelle }}</v-chip
                >
              </template>
            </v-select>
          </v-col>
        </v-row>
        <v-row dense>
          <v-col cols="12" md="12" sm="12">
            <v-text-field
              dense
              prepend-inner-icon="mdi-web"
              @click:prepend-inner="navigateToSite"
              filled
              v-model="form.siteInternet"
              :label="$t('personneMorale.detail.form.siteInternet')"
              :readonly="control.readonly"
              hide-details="auto"
              :background-color="analyseDiff.color['siteInternet']"
              :hint="analyseDiff.hint['siteInternet']"
            />
          </v-col>
        </v-row>
        <v-row dense>
          <v-col cols="12" md="12" sm="12">
            <v-expansion-panels v-model="panel" multiple>
              <v-expansion-panel>
                <v-expansion-panel-header>
                  <div>
                    <v-icon>mdi-mailbox</v-icon>
                    <span>
                      {{
                        $tc(
                          'entetes.adresse',
                          form.adresses ? form.adresses.length : 0,
                        )
                      }}
                    </span>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <adressesList
                    v-model="form.adresses"
                    :readonly="control.readonly"
                    itemPath="adresses"
                    :showHeader="false"
                    :analyseDiff="analyseDiff"
                    @annulerSuppression="annulerSuppression($event)"
                  />
                </v-expansion-panel-content>
              </v-expansion-panel>
              <v-expansion-panel>
                <v-expansion-panel-header>
                  <div>
                    <v-icon>mdi-phone-classic</v-icon>
                    <span>
                      {{
                        $tc(
                          'entetes.telephone',
                          form.telephones ? form.telephones.length : 0,
                        )
                      }}
                    </span>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <telephonesList
                    v-model="form.telephones"
                    :readonly="control.readonly"
                    :showHeader="false"
                    :itemPath="`telephones`"
                    :analyseDiff="analyseDiff"
                    @annulerSuppression="annulerSuppression($event)"
                  />
                </v-expansion-panel-content>
              </v-expansion-panel>
              <v-expansion-panel>
                <v-expansion-panel-header>
                  <div>
                    <v-icon>mdi-folder-multiple</v-icon>
                    <span>
                      {{
                        $tc(
                          'personneMorale.detail.form.panel.divisions.title',
                          divisionsLibelles
                            ? Object.keys(divisionsLibelles).length
                            : 0,
                        )
                      }}
                    </span>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-text-field
                    v-model="divisionsSearch"
                    :label="
                      $t('personneMorale.detail.form.panel.divisions.search')
                    "
                    flat
                    hide-details
                    clearable
                    clear-icon="mdi-close"
                  ></v-text-field>
                  <v-treeview
                    :items="divisions"
                    item-children="subdivisions"
                    item-key="id"
                    item-text="nom"
                    activatable
                    :search="divisionsSearch"
                    dense
                    @update:active="detailsDivisions"
                  >
                    <template #label="{item}"
                      ><a>{{ item.nom }}</a>
                    </template>
                    <template #prepend="{item, open}">
                      <v-icon
                        v-if="item.subdivisions && item.subdivisions.length > 0"
                      >
                        {{ open ? 'mdi-folder-open' : 'mdi-folder-multiple' }}
                      </v-icon>
                      <v-icon v-else>
                        {{ 'mdi-folder' }}
                      </v-icon>
                    </template>
                  </v-treeview>
                  <v-dialog v-model="divisionsDialog" scrollable width="90%">
                    <v-card>
                      <v-card-title dark class="primary white--text">
                        <v-btn icon dark @click="closeDivisionsDialog()">
                          <v-icon>mdi-close</v-icon>
                        </v-btn>
                        {{
                          $t(
                            'personneMorale.detail.form.panel.divisions.dialog.title',
                          )
                        }}
                      </v-card-title>
                      <v-card-text>
                        <v-row dense>
                          <v-col cols="12" sm="4">
                            <v-text-field
                              v-model="divisionsDialogSearch"
                              :label="
                                $t(
                                  'personneMorale.detail.form.panel.divisions.dialog.search',
                                )
                              "
                              flat
                              hide-details
                              clearable
                              clear-icon="mdi-close"
                            ></v-text-field>
                            <!--
                                      Future feature mais our l'instant vuetify est buggé sur ce point
                                    <v-col>
                                      <v-switch
                                        v-model="divisionsDialogTypeSelection"
                                        :value="divisionsDialogTypeSelection"
                                        true-value="leaf"
                                        false-value="independent"
                                        :label="$t('personneMorale.detail.form.panel.divisions.dialog.typeSelection')"></v-switch>
                                    </v-col>
                                    -->
                            <v-treeview
                              :items="divisions"
                              item-children="subdivisions"
                              item-key="id"
                              item-text="nom"
                              :search="divisionsDialogSearch"
                              selectable
                              v-model="divisionsSelectionees"
                              :value="divisionsSelectionees"
                              :selection-type="divisionsDialogTypeSelection"
                              hoverable
                              dense
                              open-all
                            ></v-treeview>
                          </v-col>
                          <v-col cols="12" md="8" sm="12">
                            <v-data-table
                              fixed-header
                              :sortBy="['nom', 'prenoms']"
                              :headers="headersContacts"
                              :items="divisionsContacts"
                              dense
                              :no-data-text="$t('noDataText')"
                              :no-results-text="
                                $t('dataIterator.noResultsText')
                              "
                              :footer-props="{
                                'items-per-page-options': [20, 50, 100],
                                'items-per-page-text': $t(
                                  'dataTable.rowsPerPageText',
                                ),
                              }"
                            >
                              <template #item.personnePhysiqueNom="{item}">
                                <navigable-link
                                  :to="
                                    $liens().personnePhysique(
                                      item.personnePhysiqueId,
                                    )
                                  "
                                >
                                  {{ item.personnePhysiqueNom }}
                                </navigable-link>
                              </template>
                              <template #item.personnePhysiquePrenoms="{item}">
                                <navigable-link
                                  :to="
                                    $liens().personnePhysique(
                                      item.personnePhysiqueId,
                                    )
                                  "
                                >
                                  {{ item.personnePhysiquePrenoms }}
                                </navigable-link>
                              </template>
                              <template #item.fonction="{item}">
                                {{ item.fonction }}
                              </template>
                              <template #item.divisionLibelle="{item}">
                                {{ item.divisionLibelle }}
                              </template>
                              <template #item.telephone="{item}">
                                {{ item.telephone }}
                              </template>
                              <template #item.mail="{item}">
                                {{ item.mail }}
                              </template>
                            </v-data-table>
                          </v-col>
                        </v-row>
                      </v-card-text>
                      <v-card-actions>
                        <v-btn text @click="closeDivisionsDialog">
                          {{ $t('button.close') }}
                        </v-btn>
                      </v-card-actions>
                    </v-card>
                  </v-dialog>
                </v-expansion-panel-content>
              </v-expansion-panel>

              <v-expansion-panel>
                <v-expansion-panel-header>
                  <div>
                    <v-icon>mdi-account-multiple</v-icon>
                    <span>
                      {{
                        $tc(
                          'personneMorale.detail.form.panel.membres.title',
                          contacts ? contacts.length : 0,
                        )
                      }}
                    </span>
                  </div></v-expansion-panel-header
                >
                <v-expansion-panel-content>
                  <v-data-table
                    :sortBy="['personnePhysiqueNom', 'personnePhysiquePrenoms']"
                    :headers="headersContacts"
                    :items="contacts"
                    dense
                    :no-data-text="$t('noDataText')"
                    :no-results-text="$t('dataIterator.noResultsText')"
                    :footer-props="{
                      'items-per-page-options': [20, 50, 100],
                      'items-per-page-text': $t('dataTable.rowsPerPageText'),
                    }"
                  >
                    <template #item.personnePhysiqueNom="{item}">
                      <navigable-link
                        :to="$liens().personnePhysique(item.personnePhysiqueId)"
                      >
                        {{ item.personnePhysiqueNom }}
                      </navigable-link>
                    </template>
                    <template #item.personnePhysiquePrenoms="{item}">
                      <navigable-link
                        :to="$liens().personnePhysique(item.personnePhysiqueId)"
                      >
                        {{ item.personnePhysiquePrenoms }}
                      </navigable-link>
                    </template>
                    <template #item.fonction="{item}">
                      {{ item.fonction }}
                    </template>
                    <template #item.divisionLibelle="{item}">
                      <a @click="detailsDivision(item.divisionId)">
                        {{ item.divisionLibelle }}
                      </a>
                    </template>
                    <template #item.telephone="{item}">
                      {{ item.telephone }}
                    </template>
                    <template #item.mail="{item}">
                      {{ item.mail }}
                    </template>
                  </v-data-table>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
            <v-card-text v-if="form.id">
              <footer-rgpd :form="this.form" genre="F" />
            </v-card-text>
            <v-divider :class="dividerClass" />
          </v-col>
        </v-row>
      </v-card-text>
      <slot name="zoneBasse" />
    </v-form>
    <slot />
  </v-card>
</template>

<script>
import ServeurRule from '@/plugins/mixins/ServeurRule';
import ValidationRules from '@/plugins/mixins/ValidationRules';
import DateFormatter from '@/plugins/mixins/DateFormatter';
import { defaultDiff } from '@/utils/diff.js';
import { getmask } from '@/components/telephone/telephoneUtil';
import VueTelInputVuetify from 'vue-tel-input-vuetify/lib/vue-tel-input-vuetify.vue';

export default {
  components: {
    VueTelInputVuetify,
  },
  mixins: [ServeurRule, ValidationRules, DateFormatter],
  inject: ['errors'],
  props: {
    filiales: {
      type: Array,
    },
    divisions: {
      type: Array,
    },
    contacts: {
      type: Array,
    },
    dsType: {
      type: Object,
    },
    enums: {
      type: Object,
    },
    form: {
      type: Object,
    },
    control: {
      type: Object,
    },
    analyseDiff: {
      type: Object,
      default: defaultDiff,
    },
  },
  data() {
    return {
      // Configuration de l'IHM
      panel: [],
      showAllFiliales: false,
      showAllMaisonsMeres: false,
      nbFilialesVisibles: 3,
      serverSearch: false,

      valid: true,
      // Libellés des divisions (map)
      divisionsLibelles: {},

      // Pour le treeview des divisions dans l'IHM principale
      divisionsSelectionees: [],
      divisionsDialog: false,
      divisionsSearch: null,

      // Pour le treeview des divisions dans le dialog
      divisionsDialogSearch: null,
      divisionsDialogTypeSelection: 'independent',
      // divisionsDialogTypeSelection: 'leaf', // v-treeview est buggé dans ce mode
      // Liste des contacts des divisions
      divisionsContacts: {},

      // Les colonnes pour les Contacts
      headersContacts: [
        {
          text: this.$t(
            'recherche.rechercheSimple.personnePhysique.list.headers.nom',
          ),
          sortable: true,
          value: 'personnePhysiqueNom',
        },
        {
          text: this.$t(
            'recherche.rechercheSimple.personnePhysique.list.headers.prenoms',
          ),
          sortable: true,
          value: 'personnePhysiquePrenoms',
        },
        {
          text: this.$t(
            'recherche.rechercheSimple.personnePhysique.list.headers.fonction',
          ),
          sortable: true,
          value: 'fonction',
        },
        {
          text: this.$t(
            'recherche.rechercheSimple.personnePhysique.list.headers.division',
          ),
          sortable: true,
          value: 'divisionLibelle',
        },
        {
          text: this.$t(
            'recherche.rechercheSimple.personnePhysique.list.headers.telephone',
          ),
          sortable: true,
          value: 'telephone',
        },
        {
          text: this.$t(
            'recherche.rechercheSimple.personnePhysique.list.headers.mail',
          ),
          sortable: true,
          value: 'mail',
        },
      ],
    };
  },

  methods: {
    getmask,
    navigateToSite() {
      var url = this.form.siteInternet;
      window.open(url, '_blank');
    },
    colorMaisonMere(id) {
      if (!this.analyseDiff) {
        return null;
      }
      const c = this.analyseDiff.color[`maisonsMeres[${id}]`];
      if (c) {
        return c;
      } else {
        return null;
      }
    },

    getForms() {
      //access aux formulaire à créer
      return [this.$refs.form];
    },

    // Gestion des maisons mères
    // Affichage de la fenêtre de séection des PMs
    selectMaisonMere() {
      this.$refs.dialogRechercheMaisonMere.show();
    },
    // Ajout d'une PM
    addMaisonMere(maisonMere) {
      if (!this.form.maisonsMeres.find(m => m.id == maisonMere.id)) {
        this.form.maisonsMeres.push({
          id: maisonMere.id,
          raisonSociale: maisonMere.raisonSociale,
          nomUsuel: maisonMere.nomUsuel,
        });
        this.form.maisonsMeres.sort((a, b) => {
          return a.nomUsuel.localeCompare(b.nomUsuel, 'fr', {
            sensitivity: 'base',
            ignorePunctuation: true,
          });
        });
        // On affiche toutes les maisons mères pour éviter que l'utilisateur ait l'impression qu'il ne s'est rien passé
        this.showAllMaisonsMeres = true;
      }
    },
    removeMaisonMere(maisonMere, index) {
      this.form.maisonsMeres.splice(index, 1);
    },

    annulerSuppression(path) {
      this.$emit('annulerSuppression', path);
    },

    // Gestion des divisions
    detailsDivisions(values) {
      if (values && Array.isArray(values)) {
        this.divisionsSelectionees = values;
        this.divisionsDialog = true;
      }
    },
    detailsDivision(value) {
      if (value) {
        this.divisionsSelectionees = Array.of(value);
        this.divisionsDialog = true;
      }
    },
    closeDivisionsDialog() {
      this.divisionsDialog = false;
      this.divisionsSelectionees = [];
    },

    // Lien vers une PM
    detailFiliale(idFiliale) {
      const route = {
        path: `/recherche/recherchesimple/personne-morale/${idFiliale}`,
        params: {
          id: idFiliale,
        },
      };
      this.$router.push(route);
    },

    // Lien externe
    lienExterne(url, onlyIfTrue) {
      if (url && url != '' && onlyIfTrue) {
        window.open(url, '_blank');
      }
    },

    // Ajoute le libellé de la division à la map, puis fait la même chose sur les subdivisions
    pushDivisionLibelle(d) {
      this.divisionsLibelles[d.id] = d.nom;
      if (d.subdivisions) {
        d.subdivisions.forEach(s => this.pushDivisionLibelle(s));
      }
    },
    setLibellesDivisions() {
      this.contacts.forEach(c => {
        c.divisionLibelle = this.divisionsLibelles[c.divisionId];
      });
    },
  },
  computed: {
    listeContextes() {
      if (this.enums.contextes.length) {
        return this.enums.contextes;
      } else {
        var contextes = [];
        if (this.form && this.form.contextes) {
          contextes = [...this.form.contextes];
        }
        return contextes;
      }
    },

    listeCotiseA() {
      if (this.enums.cotiseA.length) {
        return this.enums.cotiseA;
      } else {
        var cotiseA = [];
        if (this.form && this.form.cotiseA) {
          cotiseA = [...this.form.cotiseA];
        }
        return cotiseA;
      }
    },

    listeTypesPM() {
      if (this.enums.types.length) {
        return this.enums.types;
      } else {
        const types = [];
        if (this.form.type) {
          types.push(this.form.type);
        }
        return types;
      }
    },
    dividerClass() {
      if (this.control.enCreation || this.control.enDemandeCreation) {
        return '';
      } else if (
        this.control.enDemandeModification ||
        (!this.control.readonly &&
          !this.control.enDemandeSuppression &&
          !this.enModeReactivation &&
          this.control.edition === 'DIRECT')
      ) {
        return 'accent';
      } else if (this.control.enDemandeSuppression) {
        return 'error';
      } else {
        return '';
      }
    },
    title() {
      if (this.control.enDemandeModification) {
        return `${this.form.nomUsuel} : Demande de modification`;
      } else if (this.control.enDemandeCreation) {
        return 'Personne morale : Demande de création';
      } else if (this.control.enCreation) {
        return 'Personne morale : Création';
      } else if (
        !this.control.readonly &&
        !this.control.enDemandeSuppression &&
        !this.enModeReactivation &&
        this.control.edition === 'DIRECT'
      ) {
        return `${this.form.nomUsuel} : Modification`;
      } else if (this.control.enDemandeSuppression) {
        return `${this.form.nomUsuel} : Demande de suppression`;
      } else {
        return this.form.nomUsuel;
      }
    },
  },
  watch: {
    // Pour mettre a jour la datatable de la dialog, avec les contacts des divisions sélectionnées
    divisionsSelectionees(values) {
      this.divisionsContacts = this.contacts.filter(c =>
        values.includes(c.divisionId),
      );
    },
    // Pour mettre à jour la map des libellés des divisions et les libellés des divisions dans les contacts
    divisions(list) {
      if (list) {
        this.divisionsLibelles = {};
        list.forEach(d => this.pushDivisionLibelle(d));
      } else {
        this.divisionsLibelles = {};
      }
      if (this.contacts && Array.isArray(this.contacts)) {
        this.setLibellesDivisions();
      }
    },
    // Pour mettre à jour les libellés des divisions dans les contacts
    contacts() {
      if (this.divisions && Array.isArray(this.divsions)) {
        this.setLibellesDivisions();
      }
    },
    control: {
      handler(control) {
        // En création ou demande de création, on déplie les panels adresses et téléphones
        if (control.enCreation || control.enDemandeCreation) {
          if (!this.panel.includes(0)) {
            this.panel.push(0);
          }
          if (!this.panel.includes(1)) {
            this.panel.push(1);
          }
        }
        // En modification ou demande de modification, on déplie les panels en erreur
        else if (
          (!this.control.readonly &&
            !this.control.enDemandeSuppression &&
            !this.enModeReactivation &&
            this.control.edition === 'DIRECT') ||
          control.enDemandeModification
        ) {
          if (this.errors.fields.adresses && !this.panel.includes(0)) {
            this.panel.push(0);
          }
          if (this.errors.fields.telephones && !this.panel.includes(1)) {
            this.panel.push(1);
          }
        }
      },
      deep: true,
    },
  },
};
</script>
